import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Bell, Mail, AlertTriangle, ShieldOff, Clock, Ticket, ArrowLeftRight, Loader2, CheckCircle, MessageSquare, UserCheck, Lock, PlusCircle } from 'lucide-react';
import { toast } from 'sonner';

const ALERT_TYPES = [
  { key: 'comment_added', label: 'Staff Comments', desc: 'When a staff member posts a public comment on a ticket', icon: MessageSquare, color: 'text-cyan-500' },
  { key: 'customer_reply', label: 'Customer Replies', desc: 'When a customer replies to a ticket (email or portal)', icon: UserCheck, color: 'text-blue-500' },
  { key: 'internal_note', label: 'Internal Notes', desc: 'When an internal note is added to a ticket you\'re assigned to', icon: Lock, color: 'text-gray-500' },
  { key: 'new_ticket', label: 'New Tickets', desc: 'When a new support ticket is created', icon: PlusCircle, color: 'text-green-500' },
  { key: 'ticket_assigned', label: 'Ticket Assigned to Me', desc: 'When a new ticket is assigned to you', icon: Ticket, color: 'text-blue-500' },
  { key: 'ticket_status_change', label: 'Ticket Status Changes', desc: 'When a ticket you own changes status', icon: ArrowLeftRight, color: 'text-purple-500' },
  { key: 'sla_breach', label: 'SLA Breaches', desc: 'When a ticket breaches its SLA response or resolution time', icon: Clock, color: 'text-orange-500' },
  { key: 'sync_failure', label: 'Sync Failures', desc: 'When a device fails to sync with NinjaRMM', icon: AlertTriangle, color: 'text-amber-500' },
  { key: 'sync_auto_excluded', label: 'Auto-Excluded Devices', desc: 'When a device gets auto-excluded after repeated failures', icon: ShieldOff, color: 'text-red-500' },
];

export default function NotificationPreferences() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadPrefs(); }, []);

  const loadPrefs = async () => {
    try {
      const res = await api.get('/users/me/notification-preferences');
      setPrefs(res.data.preferences);
    } catch { toast.error('Failed to load preferences'); }
  };

  const togglePref = async (alertType, channel) => {
    const updated = { ...prefs };
    if (!updated[alertType]) updated[alertType] = { in_app: true, email: false };
    updated[alertType][channel] = !updated[alertType][channel];
    setPrefs(updated);
    setSaving(true);
    try {
      const res = await api.put('/users/me/notification-preferences', { preferences: updated });
      setPrefs(res.data.preferences);
    } catch { toast.error('Failed to save'); loadPrefs(); }
    setSaving(false);
  };

  if (!prefs) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto p-6" data-testid="notification-preferences-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Bell className="h-6 w-6" /> Notification Preferences
        </h1>
        <p className="text-gray-500 mt-1">Choose which alerts you receive and how</p>
      </div>

      <Card>
        <CardHeader>
          <div className="grid grid-cols-[1fr_80px_80px] gap-4 items-center text-sm font-medium text-gray-500 border-b pb-3">
            <span>Alert Type</span>
            <span className="text-center flex items-center justify-center gap-1"><Bell className="h-3.5 w-3.5" /> In-App</span>
            <span className="text-center flex items-center justify-center gap-1"><Mail className="h-3.5 w-3.5" /> Email</span>
          </div>
        </CardHeader>
        <CardContent className="space-y-1 -mt-2">
          {ALERT_TYPES.map(({ key, label, desc, icon: Icon, color }) => {
            const p = prefs[key] || { in_app: true, email: false };
            return (
              <div key={key} className="grid grid-cols-[1fr_80px_80px] gap-4 items-center py-3 border-b last:border-0" data-testid={`pref-row-${key}`}>
                <div className="flex items-start gap-3">
                  <Icon className={`h-5 w-5 mt-0.5 flex-shrink-0 ${color}`} />
                  <div>
                    <Label className="text-sm font-medium">{label}</Label>
                    <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                  </div>
                </div>
                <div className="flex justify-center">
                  <Switch
                    checked={p.in_app}
                    onCheckedChange={() => togglePref(key, 'in_app')}
                    data-testid={`pref-${key}-in-app`}
                  />
                </div>
                <div className="flex justify-center">
                  <Switch
                    checked={p.email}
                    onCheckedChange={() => togglePref(key, 'email')}
                    data-testid={`pref-${key}-email`}
                  />
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {saving && (
        <div className="flex items-center gap-2 text-sm text-blue-600 mt-3">
          <Loader2 className="h-4 w-4 animate-spin" /> Saving...
        </div>
      )}

      <p className="text-xs text-gray-400 mt-4">
        Changes are saved automatically. Email alerts require an active O365 mailbox connection.
      </p>
    </div>
  );
}
